// Who liked, who commented on and who has seen one post — the drill-down behind the
// clickable counts on All posts (feed card and table) and in the post review modal.
//
// ── WHY THE TAB COUNTS COME FROM THE LISTS, NOT FROM THE POST ────────────────
//
// A post's commentCount counts LIVE, published comments — what an employee sees under
// it. This list also carries removed, held, not-approved and author-deleted comments,
// because a moderator asking "who commented" means everyone who did. Labelling it with
// the post's number would put "Commented by (3)" above five rows. So each tab's count
// is its own endpoint's `total` for an EMPTY search, and all three lists are fetched
// as the panel opens so every label is right before anyone clicks it. A search narrows
// the list below, never the labels — the "n matching" line says what it narrowed to.
//
// ── ONE SEARCH, THREE LISTS ─────────────────────────────────────────────────
//
// The box applies to whichever tab is showing, and each list remembers the query it
// was loaded for: switching tabs with a search typed fetches that tab for the same
// query, and a list already loaded for it is reused. A generation counter per tab drops
// any response that a newer query or tab visit has overtaken, so a slow first answer
// can never overwrite the list for what is typed now.
//
// Rendered by its callers through a portal, like every modal on the page: the scaffold
// clips overflow. z-[60] so it stacks above ReviewModal (z-50) when opened from there.

import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X, Heart, MessageCircle, Eye, Search, Download, Loader2, AlertCircle } from 'lucide-react';
import { BlockLoader } from '../loading';
import { buildCsv, downloadCsv } from '../../utils/csv';
import {
  connectPolicyErrorMessage,
  connectPolicyErrorStatus,
  parseServerTimestamp,
} from '../../services/connectPolicyApi';
import {
  engagementAPI,
  engagementPhotoUrl,
  engagementRowTime,
  isEngagementComment,
  ENGAGEMENT_TABS,
  type EngagementCommentStatus,
  type EngagementRow,
  type EngagementTab,
} from '../../services/engagementApi';

// ─────────────────────────────────────────────────────────────────────────────
// Shared presentation — also used by the feed card, the All posts table and the
// review modal, so a count reads the same wherever it is clicked.

/** 1,079 — and, being en-IN, 1,23,456 past a lakh. A missing count (an API build
 *  that predates it) renders as a dash rather than a misleading 0. Coerced, because
 *  a Postgres bigint count arrives as a string whatever the type says. */
export const fmtCount = (n: number | null | undefined): string => {
  const v = n == null ? NaN : Number(n);
  return Number.isFinite(v) ? v.toLocaleString('en-IN') : '—';
};

const TAB_META: Record<EngagementTab, {
  label: string;
  icon: typeof Heart;
  /** Singular / plural noun for a labelled count ("12 likes"). */
  noun: [string, string];
  /** Tooltip and accessible name of a count that opens this tab. */
  hint: string;
  empty: string;
  /** CSV column for the row's timestamp, and the file-name slug. */
  timeColumn: string;
  fileSlug: string;
}> = {
  likes: {
    label: 'Liked by', icon: Heart, noun: ['like', 'likes'], hint: 'See who liked this',
    empty: 'No one has liked this post yet.', timeColumn: 'Liked On', fileSlug: 'liked-by',
  },
  comments: {
    label: 'Commented by', icon: MessageCircle, noun: ['comment', 'comments'], hint: 'See who commented',
    empty: 'No one has commented on this post yet.', timeColumn: 'Commented On', fileSlug: 'commented-by',
  },
  viewers: {
    label: 'Seen by', icon: Eye, noun: ['view', 'views'], hint: 'See who has seen this',
    empty: 'No one has seen this post yet.', timeColumn: 'First Seen On', fileSlug: 'seen-by',
  },
};

/** Same colours as the page's STATUS_META, so "Removed" means one thing on screen.
 *  No entry for 'live' — a live comment is the norm and carries no chip. */
const COMMENT_STATUS_META: Record<Exclude<EngagementCommentStatus, 'live'>, { label: string; cls: string }> = {
  removed: { label: 'Removed', cls: 'bg-red-100 text-red-700' },
  held: { label: 'Held for review', cls: 'bg-amber-100 text-amber-700' },
  not_approved: { label: 'Not approved', cls: 'bg-rose-50 text-rose-700' },
  author_deleted: { label: 'Author deleted', cls: 'bg-gray-100 text-gray-600' },
};

const commentStatusLabel = (s: EngagementCommentStatus): string =>
  s === 'live' ? 'Live' : COMMENT_STATUS_META[s]?.label ?? s;

/**
 * "♥ 12 · 💬 3 · 👁 1,079", each number a button that opens the panel on its tab.
 * Stops propagation so a click inside a clickable table row opens the list, not the
 * row's review modal. Inherits font size and colour from where it sits.
 *
 * Compact (the table cell) drops the dots, like the feed card's footer: a squeezed
 * column wraps the three counts, and a dot left at the end of a line reads as a
 * missing fourth number. Kept on one line it widens the table past the Actions
 * column at 1280px, so it wraps rather than overflows.
 */
export function EngagementCounts({ likes, comments, views, onOpen, labelled = false }: {
  likes: number | null | undefined;
  comments: number | null | undefined;
  views: number | null | undefined;
  onOpen: (tab: EngagementTab) => void;
  /** "12 likes" rather than "12" — for the review modal, where there is room. */
  labelled?: boolean;
}) {
  const counts: Record<EngagementTab, number | null | undefined> = { likes, comments, viewers: views };
  return (
    <span className={`inline-flex flex-wrap items-center gap-y-0.5 ${labelled ? 'gap-x-0.5' : 'gap-x-1.5'}`}>
      {ENGAGEMENT_TABS.map((t, i) => {
        const meta = TAB_META[t];
        const Icon = meta.icon;
        const n = counts[t];
        const noun = n === 1 ? meta.noun[0] : meta.noun[1];
        return (
          <Fragment key={t}>
            {i > 0 && labelled && <span aria-hidden="true" className="text-gray-300">·</span>}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpen(t); }}
              title={meta.hint}
              aria-label={`${fmtCount(n)} ${noun} — ${meta.hint.toLowerCase()}`}
              className="inline-flex items-center gap-1 rounded px-1 py-0.5 tabular-nums hover:bg-gray-100 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <Icon className="h-3.5 w-3.5" />
              {fmtCount(n)}{labelled ? ` ${noun}` : ''}
            </button>
          </Fragment>
        );
      })}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatting.

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';

const relTime = (d: Date): string => {
  const secs = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  const days = Math.floor(secs / 86400);
  if (days < 7) return `${days}d ago`;
  if (days < 35) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

// IST, like every other absolute time on the page.
const absTime = (d: Date): string =>
  d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
  });

/** Through parseServerTimestamp, not `new Date`: these routes can hand back raw
 *  Postgres TIMESTAMPTZ text, which Safari refuses to parse. */
const csvTime = (value: string | null | undefined): string => {
  const d = parseServerTimestamp(value);
  return d ? absTime(d) : '-';
};

/** A cell that opens with = + - @ is run as a formula by Excel and Sheets. Names and
 *  comments are typed by employees, so they are defused with a leading apostrophe. */
const sheetSafe = (value: string | null | undefined): string => {
  const s = String(value ?? '');
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
};

const pad2 = (n: number): string => String(n).padStart(2, '0');
const todayStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** Stable key across pages: a person likes or first-sees a post once, but can
 *  comment on it many times. */
const rowKey = (row: EngagementRow): string =>
  isEngagementComment(row) ? `c:${row.commentId}` : `p:${row.empCode}`;

// ─────────────────────────────────────────────────────────────────────────────
// Loading state.

interface ListError {
  text: string;
  /** False for a refusal (403 / 404), where trying again cannot help. */
  retry: boolean;
}

interface ListState {
  /** The query this list was loaded for; null = never requested. */
  q: string | null;
  rows: EngagementRow[];
  /** Matching `q`. */
  total: number | null;
  nextCursor: string | null;
  loading: boolean;
  error: ListError | null;
  loadingMore: boolean;
  moreError: ListError | null;
}

const EMPTY_LIST: ListState = {
  q: null, rows: [], total: null, nextCursor: null, loading: false, error: null, loadingMore: false, moreError: null,
};

const PAGE_SIZE = 50;

const toListError = (err: unknown, fallback = 'Could not load this list.'): ListError => {
  const status = connectPolicyErrorStatus(err);
  if (status === 403) return { text: "You don't have moderation access for this project", retry: false };
  // The controller's own 404 is JSON ("Not found": the post is not in this project).
  // A bare 404 page is an API build without these routes, which is not about the post.
  const body = (err as { response?: { data?: unknown } })?.response?.data;
  if (status === 404 && body && typeof body === 'object') {
    return { text: 'This post was not found in the selected project.', retry: false };
  }
  return { text: connectPolicyErrorMessage(err, fallback), retry: true };
};

// ═════════════════════════════════════════════════════════════════════════════

/** What the panel needs to know about the post: which one, and enough to say whose. */
export interface EngagementPanelPost {
  postId: string;
  author?: { empCode?: string | null; name?: string | null } | null;
  /** The post text (full or preview); clipped to two lines in the header. */
  content?: string | null;
}

interface Props {
  projectId: number;
  post: EngagementPanelPost;
  initialTab: EngagementTab;
  onClose: () => void;
}

type ByTab<T> = Record<EngagementTab, T>;

export default function EngagementPanel({ projectId, post, initialTab, onClose }: Props) {
  const [tab, setTab] = useState<EngagementTab>(initialTab);
  const [search, setSearch] = useState('');
  /** `search`, trimmed and debounced — what the lists are actually loaded for. */
  const [q, setQ] = useState('');
  const [lists, setLists] = useState<ByTab<ListState>>({ likes: EMPTY_LIST, comments: EMPTY_LIST, viewers: EMPTY_LIST });
  /** The unfiltered totals, i.e. the tab labels. Only a load with an empty search
   *  writes here. */
  const [totals, setTotals] = useState<ByTab<number | null>>({ likes: null, comments: null, viewers: null });
  const [download, setDownload] = useState<{ tab: EngagementTab; fetched: number } | null>(null);
  const [downloadNote, setDownloadNote] = useState<{ tab: EngagementTab; tone: 'info' | 'error'; text: string } | null>(null);

  const genRef = useRef<ByTab<number>>({ likes: 0, comments: 0, viewers: 0 });
  /** The generation whose unfiltered total the tab label shows. A label only moves
   *  forward: an older empty-search response landing after a newer one is dropped. */
  const totalGenRef = useRef<ByTab<number>>({ likes: 0, comments: 0, viewers: 0 });
  const acceptTotal = useCallback((which: EngagementTab, gen: number, total: number) => {
    if (gen < totalGenRef.current[which]) return;
    totalGenRef.current[which] = gen;
    setTotals((prev) => ({ ...prev, [which]: total }));
  }, []);
  /** The query each list was last REQUESTED for, set synchronously — so the load
   *  effect running twice (StrictMode, or a re-render before the state lands) does
   *  not fire the same request twice. Cleared on failure so a revisit retries. */
  const requestedRef = useRef<ByTab<string | null>>({ likes: null, comments: null, viewers: null });
  const downloadAbortRef = useRef<AbortController | null>(null);

  const loadFirst = useCallback(async (which: EngagementTab, query: string) => {
    const gen = ++genRef.current[which];
    requestedRef.current[which] = query;
    setLists((prev) => ({ ...prev, [which]: { ...EMPTY_LIST, q: query, loading: true } }));
    try {
      const page = await engagementAPI.list(projectId, post.postId, which, { q: query, limit: PAGE_SIZE });
      // Before the staleness check: an unfiltered total is the tab's label whether or
      // not its rows are still wanted. Typing a search, then switching tabs, while the
      // opening fetch is in flight would otherwise leave that label blank until the
      // search was cleared. Not before a NEWER empty search's total, though.
      if (!query) acceptTotal(which, gen, page.total);
      if (gen !== genRef.current[which]) return;
      setLists((prev) => ({
        ...prev,
        [which]: { ...EMPTY_LIST, q: query, rows: page.rows, total: page.total, nextCursor: page.nextCursor },
      }));
    } catch (err) {
      if (gen !== genRef.current[which]) return;
      requestedRef.current[which] = null;
      setLists((prev) => ({ ...prev, [which]: { ...EMPTY_LIST, q: query, error: toListError(err) } }));
    }
  }, [projectId, post.postId, acceptTotal]);

  // The other two tabs, unfiltered, for their labels. The shown tab loads below.
  useEffect(() => {
    ENGAGEMENT_TABS.forEach((t) => {
      if (t !== initialTab && requestedRef.current[t] === null) void loadFirst(t, '');
    });
    // Once per panel: `initialTab` is where it opened, not state to follow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFirst]);

  // The shown tab, for what is typed now.
  useEffect(() => {
    if (requestedRef.current[tab] === q) return;
    void loadFirst(tab, q);
  }, [tab, q, loadFirst]);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Closing mid-download abandons it: a file arriving after the panel is gone would
  // be a surprise, and the remaining pages are requests nobody is waiting for.
  useEffect(() => () => downloadAbortRef.current?.abort(), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const loadMore = async () => {
    const which = tab;
    const cur = lists[which];
    if (!cur.nextCursor || cur.loading || cur.loadingMore) return;
    // Not bumped: a newer query bumps it, and then this page belongs to a list that
    // is no longer on screen.
    const gen = genRef.current[which];
    setLists((prev) => ({ ...prev, [which]: { ...prev[which], loadingMore: true, moreError: null } }));
    try {
      const page = await engagementAPI.list(projectId, post.postId, which, {
        q: cur.q ?? '', cursor: cur.nextCursor, limit: PAGE_SIZE,
      });
      if (gen !== genRef.current[which]) return;
      setLists((prev) => {
        const p = prev[which];
        // De-duplicated because the list is live: someone who likes the post while a
        // moderator scrolls can otherwise appear on two pages.
        const seen = new Set(p.rows.map(rowKey));
        return {
          ...prev,
          [which]: {
            ...p,
            rows: [...p.rows, ...page.rows.filter((r) => !seen.has(rowKey(r)))],
            total: page.total,
            nextCursor: page.nextCursor,
            loadingMore: false,
          },
        };
      });
      if (!cur.q) acceptTotal(which, gen, page.total);
    } catch (err) {
      if (gen !== genRef.current[which]) return;
      setLists((prev) => ({ ...prev, [which]: { ...prev[which], loadingMore: false, moreError: toListError(err) } }));
    }
  };

  /** Every row of the shown tab for the current search — not just the pages scrolled
   *  so far — up to ENGAGEMENT_EXPORT_CAP, then saved as a CSV. */
  const downloadList = async () => {
    if (download) return;
    const which = tab;
    const query = q;
    const ctrl = new AbortController();
    downloadAbortRef.current = ctrl;
    setDownload({ tab: which, fetched: 0 });
    setDownloadNote(null);
    try {
      const res = await engagementAPI.listAll(projectId, post.postId, which, {
        q: query,
        signal: ctrl.signal,
        onProgress: (fetched) => { if (!ctrl.signal.aborted) setDownload({ tab: which, fetched }); },
      });
      if (ctrl.signal.aborted) return;
      const base = ['Name', 'Employee Code', 'Designation', 'Store'];
      const person = (r: EngagementRow) => [
        sheetSafe(r.name || r.empCode), r.empCode, sheetSafe(r.designation) || '-', sheetSafe(r.storeName) || '-',
      ];
      const headers = which === 'comments'
        ? [...base, TAB_META[which].timeColumn, 'Comment', 'Comment Status']
        : [...base, TAB_META[which].timeColumn];
      const data = res.rows.map((r) => isEngagementComment(r)
        ? [...person(r), csvTime(r.createdAt), sheetSafe(r.content) || '-', commentStatusLabel(r.status)]
        : [...person(r), csvTime(engagementRowTime(r))]);
      downloadCsv(
        `post-${post.postId.slice(0, 8)}-${TAB_META[which].fileSlug}${query ? '-search' : ''}-${todayStr()}.csv`,
        buildCsv(headers, data),
      );
      if (res.truncated) {
        setDownloadNote({
          tab: which,
          tone: 'info',
          text: `Downloaded the first ${fmtCount(res.rows.length)} of ${fmtCount(res.total)}. Search by name or code to download the rest.`,
        });
      }
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setDownloadNote({ tab: which, tone: 'error', text: toListError(err, 'The download could not be prepared.').text });
    } finally {
      if (downloadAbortRef.current === ctrl) downloadAbortRef.current = null;
      if (!ctrl.signal.aborted) setDownload(null);
    }
  };

  const cur = lists[tab];
  const meta = TAB_META[tab];
  const authorName = post.author?.name || post.author?.empCode || 'Unknown';
  const showCode = !!post.author?.empCode && post.author.empCode !== authorName;
  // Before the effect has fired for a new tab or query, the list on hand belongs to
  // something else — show the spinner rather than a moment of the wrong rows.
  const stale = cur.q !== q;
  const unfiltered = totals[tab];

  return (
    // Close only on a DIRECT backdrop click: when this is stacked over ReviewModal,
    // the click must not bubble on and close that too.
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex h-[85vh] max-h-[760px] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label="Post engagement"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — whose post, and which */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 p-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900">
              {authorName}
              {showCode && <span className="font-mono text-xs font-normal text-gray-400">{post.author?.empCode}</span>}
            </div>
            <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap break-words text-sm text-gray-500">
              {post.content?.trim() || <span className="italic text-gray-400">No text</span>}
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs, search, download */}
        <div className="shrink-0 space-y-3 border-b border-gray-100 px-5 py-3">
          <div role="tablist" className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
            {ENGAGEMENT_TABS.map((t) => {
              const Icon = TAB_META[t].icon;
              return (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  onClick={() => setTab(t)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                    tab === t ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {TAB_META[t].label}{totals[t] != null ? ` (${fmtCount(totals[t])})` : ''}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or employee code…"
                aria-label="Search by name or employee code"
                className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <button
              type="button"
              onClick={downloadList}
              disabled={download != null || stale || cur.loading || !cur.total}
              title={q ? `Download everyone on this list matching “${q}”` : 'Download everyone on this list'}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {download ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {download
                ? `Preparing… ${download.fetched > 0 ? fmtCount(download.fetched) : ''}`.trim()
                : 'Download CSV'}
            </button>
          </div>

          {downloadNote && downloadNote.tab === tab && (
            <div className={`rounded-md px-3 py-2 text-xs ${
              downloadNote.tone === 'error' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`}>
              {downloadNote.text}
            </div>
          )}
        </div>

        {/* The list */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {stale || cur.loading ? (
            <BlockLoader className="h-40" />
          ) : cur.error ? (
            <div className="px-6 py-12 text-center">
              <AlertCircle className="mx-auto mb-2 h-7 w-7 text-gray-300" />
              <div className="text-sm font-medium text-gray-700">{cur.error.text}</div>
              {cur.error.retry && (
                <button
                  type="button"
                  onClick={() => void loadFirst(tab, q)}
                  className="mt-3 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Try again
                </button>
              )}
            </div>
          ) : cur.rows.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500">
              {q ? `No one matching “${q}”.` : meta.empty}
            </div>
          ) : (
            <>
              {q && (
                <div className="px-5 pt-3 text-xs text-gray-500">
                  {fmtCount(cur.total)} matching “{q}”{unfiltered != null ? ` of ${fmtCount(unfiltered)}` : ''}
                </div>
              )}
              <ul className="divide-y divide-gray-100">
                {cur.rows.map((r) => <PersonRow key={rowKey(r)} row={r} />)}
              </ul>
              <div className="flex flex-col items-center gap-2 px-5 py-4 text-xs text-gray-400">
                {cur.moreError && <div className="text-red-600">{cur.moreError.text}</div>}
                {cur.nextCursor ? (
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={cur.loadingMore}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {cur.loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                    {cur.loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                ) : null}
                <span>
                  Showing {fmtCount(cur.rows.length)}{cur.total != null ? ` of ${fmtCount(Math.max(cur.total, cur.rows.length))}` : ''}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── One person ───────────────────────────────────────────────────────────────

function PersonRow({ row }: { row: EngagementRow }) {
  const at = parseServerTimestamp(engagementRowTime(row));
  const name = row.name || row.empCode;
  const place = [row.designation, row.storeName].filter(Boolean).join(' · ');
  const comment = isEngagementComment(row) ? row : null;
  const chip = comment && comment.status !== 'live' ? COMMENT_STATUS_META[comment.status] : null;
  return (
    <li className="flex gap-3 px-5 py-3">
      <PersonAvatar name={name} photo={row.photo} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="truncate text-sm font-medium text-gray-900">{name}</span>
              {name !== row.empCode && <span className="font-mono text-[11px] text-gray-400">{row.empCode}</span>}
              {chip && (
                <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${chip.cls}`}>
                  {chip.label}
                </span>
              )}
            </div>
            <div className="truncate text-xs text-gray-500">{place || 'No store on record'}</div>
          </div>
          {at && (
            <div className="shrink-0 text-right">
              <div className="text-xs text-gray-600">{relTime(at)}</div>
              <div className="whitespace-nowrap text-[11px] text-gray-400">{absTime(at)}</div>
            </div>
          )}
        </div>
        {comment && <ClampedText text={comment.content ?? ''} muted={comment.status !== 'live'} />}
      </div>
    </li>
  );
}

/** Their HR photo when there is one that loads, their initials otherwise. */
function PersonAvatar({ name, photo }: { name: string; photo: string | null }) {
  const url = engagementPhotoUrl(photo);
  const [failed, setFailed] = useState(false);
  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className="h-9 w-9 flex-none rounded-full bg-gray-100 object-cover"
      />
    );
  }
  return (
    <div className="grid h-9 w-9 flex-none place-items-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700">
      {initials(name)}
    </div>
  );
}

/** A comment clamped to three lines, with Show more only when it actually overflows —
 *  measured, not guessed from the character count, because line length depends on
 *  the panel's width and on newlines. */
function ClampedText({ text, muted }: { text: string; muted: boolean }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !expanded) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  if (!text.trim()) return <p className="mt-1.5 text-xs italic text-gray-400">No text</p>;
  return (
    <div className="mt-1.5 rounded-lg bg-gray-50 px-3 py-2">
      <p
        ref={ref}
        className={`whitespace-pre-wrap break-words text-sm ${muted ? 'text-gray-500' : 'text-gray-700'} ${
          expanded ? '' : 'line-clamp-3'}`}
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-medium text-primary-600 hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}
